import {
  Stack,
  Typography,
  styled,
  type CSSObject,
  GridLegacy as Grid,
} from '@mui/material';
import { Button, Section } from '@rumblefish/ui/Rumblefish23Theme';
import { fluidSize } from '@rumblefish/ui/src/theme/rumblefish23Theme/utils';

export const HeroSection = styled(Section)(() => ({
  position: 'relative',
}));

export const AmbientBackground = styled('div')(({ theme }) => ({
  position: 'absolute',
  inset: 0,
  overflow: 'hidden',
  zIndex: 0,
  pointerEvents: 'none',

  '&::before, &::after': {
    content: '""',
    position: 'absolute',
    borderRadius: '50%',
    filter: 'blur(120px)',
    opacity: 0.4,
    willChange: 'transform',
  },
  '&::before': {
    width: '45vw',
    height: '45vw',
    top: '-15%',
    right: '-10%',
    background: `radial-gradient(circle, ${theme.palette.blueColors?.PLUS_1} 0%, transparent 70%)`,
    animation: 'heroAmbientDrift1 20s ease-in-out infinite',
  },
  '&::after': {
    width: '38vw',
    height: '38vw',
    bottom: '-20%',
    left: '0%',
    background: `radial-gradient(circle, ${theme.palette.raspberryColors?.PLUS_1} 0%, transparent 70%)`,
    animation: 'heroAmbientDrift2 24s ease-in-out infinite',
  },

  '@keyframes heroAmbientDrift1': {
    '0%, 100%': { transform: 'translate(0, 0) scale(1)' },
    '50%': { transform: 'translate(-6%, 8%) scale(1.15)' },
  },
  '@keyframes heroAmbientDrift2': {
    '0%, 100%': { transform: 'translate(0, 0) scale(1)' },
    '50%': { transform: 'translate(8%, -6%) scale(1.1)' },
  },
  '@media (prefers-reduced-motion: reduce)': {
    '&::before, &::after': { animation: 'none' },
  },
}));

export const StyledGrid = styled(Grid)(() => ({
  padding: '0',
  margin: '0',
  listStyle: 'none',
  display: 'flex',
  WebkitFlexFlow: 'row',
  lineHeight: '30px',
}));

export const BubblesStyledStack = styled(Stack)(({ theme }) => ({
  width: '40%',
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  transform: 'none',
  [theme.breakpoints.down('xl')]: {
    width: '29%',
  },
  [theme.breakpoints.down('md')]: {
    transform: `translateX(${theme.utils.fluidSize({
      minSize: -40,
      maxSize: 550,
    })})`,
    width: 'auto',
    position: 'absolute',
    zIndex: -1,
    filter: 'blur(4.5px)',
  },
}));

export const TextStyledStack = styled(Stack)(({ theme }) => ({
  width: '60%',
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(4),
  [theme.breakpoints.down('xl')]: {
    width: '71%',
  },
  [theme.breakpoints.down('md')]: {
    width: '100%',
  },
}));

export const StyledRowGrid = styled(Grid)(({ theme }) => ({
  margin: '1px',
  minWidth: '65px',
  position: 'relative',
  flex: '1 0 auto',
  height: 'auto',
  display: 'flex',
  transform: 'translateX(-30%)',
  alignItems: 'center',
  [theme.breakpoints.down('xl')]: {
    transform: 'translateX(-45%)',
  },
  [theme.breakpoints.down('lg')]: {
    transform: 'translateX(-110%)',
    maxHeight: '120px',
    minWidth: '60px',
  },
  [theme.breakpoints.down('md')]: {
    width: '120px',
    height: '130px',
    transform: 'translateX(20%)',
  },
  '&:before': {
    content: "''",
    paddingTop: theme.utils.fluidSize({
      minSize: 100,
      maxSize: 145,
      maxBp: 1500,
    }),
    [theme.breakpoints.up('xl')]: {
      paddingTop: '145px',
    },
  },
}));

export const StyledStack = styled(Stack)(({ theme }) => ({
  position: 'relative',
  zIndex: 1,
  padding: theme.spacing(9, 0),
  width: '100%',
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(4),
  [theme.breakpoints.down('md')]: {
    padding: theme.spacing(6, 0, 9, 0),
  },
  [theme.breakpoints.down('sm')]: {
    padding: theme.spacing(4, 0, 9, 0),
  },
}));

export const StyledIntroStack = styled(Stack)(({ theme }) => ({
  width: '100%',
  display: 'flex',
  flexDirection: 'row',
  marginBottom: theme.spacing(2),
}));

export const CompaniesStyledStack = styled(Stack)(({ theme }) => ({
  width: '100%',
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(7),
  marginTop: theme.spacing(2),
  textAlign: 'center',
}));

export const ButtonSectionStack = styled(Stack)(() => ({
  width: '100%',
  display: 'flex',
}));

export const StyledButton = styled(Button)(({ theme }) => ({
  '.MuiStack-root': {
    position: 'relative',
    overflow: 'hidden',
    background: 'rgba(255, 255, 255, 0.08) !important',
    backdropFilter: 'blur(12px)',
    border: `1px solid ${theme.palette.raspberryColors?.PLUS_1}55`,
    boxShadow: `0px 4px 24px 0px ${theme.palette.raspberryColors?.PLUS_2}33, inset 0px 1px 0px 0px rgba(255, 255, 255, 0.25)`,
    transition: 'border-color 0.25s ease, box-shadow 0.25s ease',
    '&:hover': {
      borderColor: `${theme.palette.raspberryColors?.PLUS_1}aa`,
      boxShadow: `0px 6px 28px 0px ${theme.palette.raspberryColors?.PLUS_2}55, inset 0px 1px 0px 0px rgba(255, 255, 255, 0.35)`,
    },
  },
}));

export const StyledTitle = styled(Typography)(({ theme }) => ({
  ...(theme.mixins.neutralGradient as CSSObject),
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'flex-start',
  backgroundClip: 'text',
  WebkitBackgroundClip: 'text',
  WebkitTextFillColor: 'transparent',
  marginBottom: theme.spacing(2),

  [theme.breakpoints.down('sm')]: {
    width: '100%',
  },
}));

export const StyledHeroHeadingWrapper = styled(Typography)(({ theme }) => ({
  ...(theme.mixins.neutralGradient as CSSObject),
  width: '110%',
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'flex-start',
  backgroundClip: 'text',
  WebkitBackgroundClip: 'text',
  WebkitTextFillColor: 'transparent',

  '& > ol': {
    position: 'relative',
    listStyle: 'none',
    margin: 0,
    padding: 0,
    height: `${fluidSize({ minSize: 48, maxSize: 88 })}`,
    overflow: 'hidden',
    isolation: 'isolate',
    transform: 'translateZ(0)',
  },

  '& > ol li': {
    height: `110%`,
    transform: `translateY(calc(-100% * var(--word-counter) - 5%)) translateZ(0)`,
    backfaceVisibility: 'hidden',
  },

  '& > ol li span': {
    height: `100%`,
  },

  [theme.breakpoints.up('xl')]: {
    '& > ol': {
      height: '88px',
    },
  },
  [theme.breakpoints.down('sm')]: {
    '& > ol': {
      height: '90px',
    },
    '& > ol li span': {
      display: 'flex',
      alignItems: 'flex-end',
    },
  },
}));

export const StyledHeroHeading = styled(Typography)(({ theme }) => ({
  ...(theme.mixins.neutralGradient as CSSObject),
  display: 'flex',
  justifyContent: 'flex-start',
  backgroundClip: 'text',
  WebkitBackgroundClip: 'text',
  WebkitTextFillColor: 'transparent',
}));

export const StyledDescription = styled(Typography)(({ theme }) => ({
  color: theme.palette.invertedContent?.TETRIARY,
}));

export const StyledSpan = styled(Typography)(({ theme }) => ({
  color: theme.palette.invertedContent?.PRIMARY,
  whiteSpace: 'nowrap',
}));
